import { ReactNode } from 'react';
import { ChevronRight, LucideIcon } from 'lucide-react';

interface Breadcrumb {
  label: string;
  onClick?: () => void;
}

interface PageHeaderProps {
  eyebrow?: string;
  eyebrowIcon?: LucideIcon;
  title: string;
  description?: string;
  breadcrumbs?: Breadcrumb[];
  actions?: ReactNode;
}

export function PageHeader({
  eyebrow,
  eyebrowIcon: Icon,
  title,
  description,
  breadcrumbs,
  actions,
}: PageHeaderProps) {
  return (
    <header className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-6 md:mb-8">
      <div className="min-w-0">
        {breadcrumbs && breadcrumbs.length > 0 && (
          <nav aria-label="Migas" className="mb-2 flex flex-wrap items-center gap-1 text-sm">
            {breadcrumbs.map((b, i) => {
              const isLast = i === breadcrumbs.length - 1;
              return (
                <span key={`${b.label}-${i}`} className="flex items-center gap-1">
                  {b.onClick && !isLast ? (
                    <button
                      type="button"
                      onClick={b.onClick}
                      className="text-muted hover:text-cubus-lime transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cubus-lime rounded px-1"
                    >
                      {b.label}
                    </button>
                  ) : (
                    <span className={isLast ? 'text-white/90' : 'text-muted'}>{b.label}</span>
                  )}
                  {!isLast && <ChevronRight className="w-3.5 h-3.5 text-faint" aria-hidden="true" />}
                </span>
              );
            })}
          </nav>
        )}
        {eyebrow && (
          <div className="chip chip-lime mb-3">
            {Icon && <Icon className="w-3.5 h-3.5" aria-hidden="true" />}
            {eyebrow}
          </div>
        )}
        <h1 className="text-display text-3xl md:text-4xl font-bold text-white tracking-tight">
          {title}
        </h1>
        {description && <p className="mt-2 text-muted max-w-2xl">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}
